import { reportHandledException } from '@/lib/error-reporting'

/**
 * The referral code captured from a `?ref=CODE` link (issue #71), kept in a
 * first-party cookie for 14 days and read at signup-verify / guest-checkout
 * time, where the client sends it in the request payload. The server never
 * reads this cookie.
 *
 * `kv_ref` sits with the other first-party identity cookies (`kv_did`,
 * `kv_ft`): host-only, `Path=/`, `SameSite=Lax`, `Secure` on https, readable
 * by JS because the page is the only consumer.
 *
 * The code is stored NORMALIZED (trimmed + uppercased) to match the server's
 * `normalizeCode`, and bounded to 16 chars (the column width) so a junk-laden
 * `?ref` can't bloat the cookie.
 */
export const REFERRAL_CODE_COOKIE = 'kv_ref'

const MAX_AGE_SECONDS = 60 * 60 * 24 * 14

const MAX_CODE_LENGTH = 16

function normalize(code: string): string {
  return code.trim().toUpperCase().slice(0, MAX_CODE_LENGTH)
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  if (!match) return null
  try {
    return decodeURIComponent(match[1])
  } catch (cause) {
    // Any script on the origin can plant a malformed value; treat it as no code.
    reportHandledException(cause, { source: 'features/referral/referral-storage.ts' })
    return null
  }
}

function writeCookie(value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${REFERRAL_CODE_COOKIE}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`
}

export function readReferralCode(): string | null {
  migrateLegacyReferralCode()
  const value = readCookie(REFERRAL_CODE_COOKIE)
  if (!value) return null
  const normalized = normalize(value)
  return normalized.length > 0 ? normalized : null
}

export function storeReferralCode(code: string): void {
  const normalized = normalize(code)
  if (normalized.length === 0) return
  writeCookie(normalized, MAX_AGE_SECONDS)
}

export function clearReferralCode(): void {
  writeCookie('', 0)
  legacyStorage()?.removeItem(LEGACY_REFERRAL_CODE_KEY)
}

// ---------------------------------------------------------------------------
// Legacy localStorage copy. Delete this whole block, plus the
// `migrateLegacyReferralCode()` line in readReferralCode and the
// `legacyStorage()` line in clearReferralCode, once the cookie release has been
// live on web for 14 days.
// ---------------------------------------------------------------------------

export const LEGACY_REFERRAL_CODE_KEY = 'kiao-voice:referral-code'

function legacyStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  return window.localStorage
}

/**
 * Move a code saved in localStorage into the cookie, with a fresh 14 days, and
 * drop the localStorage copy. A cookie that already holds a code wins.
 */
export function migrateLegacyReferralCode(): void {
  const storage = legacyStorage()
  const legacy = storage?.getItem(LEGACY_REFERRAL_CODE_KEY)
  if (legacy === null || legacy === undefined) return
  if (!readCookie(REFERRAL_CODE_COOKIE)) storeReferralCode(legacy)
  storage?.removeItem(LEGACY_REFERRAL_CODE_KEY)
}
